import React, { useMemo } from 'react';

import { fade } from '@material-ui/core';
import { AxisProps } from '@nivo/axes';
import { ResponsiveBar } from '@nivo/bar';
import { Theme as ThemeProps } from '@nivo/core';
import { timeFormat } from 'd3-time-format';
import cloneDeep from 'lodash/cloneDeep';
import { AutoSizer } from 'react-virtualized';

import { colorsPalette } from '../../theme';
import { calcComplementedPoints, IPoint } from '../LineChart/helpers';
import useStyles from './styles';

const BarChart = ({
  data,
  axis = false,
  chosen = false,
  groupBy = 'month',
  enableGridY = false,
  from = null,
  to = null,
  name = '',
}) => {
  const classes = useStyles();

  const clonedData = useMemo(() => {
    const dateFrom = from && new Date(from!);
    const dateTo = to && new Date(to!);
    const cloned = cloneDeep(data);

    const [pointsBefore = [], pointsAfter = []] = calcComplementedPoints({
      points: cloned,
      groupBy,
      from: dateFrom,
      to: dateTo,
    });

    return [...(pointsBefore as IPoint[]), ...cloned, ...(pointsAfter as IPoint[])];
  }, [data, groupBy, from, to]);

  if (clonedData && clonedData.length > 0 && chosen) {
    clonedData.forEach((item) => {
      item.color = '#6869ED';
    });
  }

  const format = (value) => {
    const formatString = groupBy === 'month' ? `%b '%y` : `%d %b '%y`;

    return timeFormat(formatString)(new Date(value));
  };

  const tickValues = `every ${groupBy}`;

  const axisBottom: AxisProps | null = axis
    ? {
        format: format,
        orient: 'bottom',
        tickSize: 5,
        tickPadding: 20,
        tickRotation: -70,
        legend: '',
        legendOffset: -40,
        legendPosition: 'middle',
        tickValues: tickValues,
      }
    : null;

  const axisLeft: AxisProps | null = axis
    ? {
        orient: 'left',
        tickSize: 5,
        tickPadding: 20,
        tickRotation: 0,
        legend: '',
        legendOffset: -40,
        legendPosition: 'middle',
      }
    : null;

  const theme: ThemeProps = {
    tooltip: {
      container: {
        background: 'rgba(163, 163, 245, 1)',
      },
    },
    axis: {
      ticks: {
        line: {
          stroke: '#FFFFFF30',
        },
        text: {
          fill: colorsPalette.white,
          fontSize: '14px',
        },
      },
      domain: {
        line: {
          stroke: '#FFFFFF30',
        },
      },
    },
    grid: {
      line: {
        stroke: fade(colorsPalette.white, 0.19),
        strokeWidth: 1,
      },
    },
  };

  const margin = chosen
    ? { top: 10, right: 20, bottom: 110, left: 70 }
    : { top: 10, right: 3, bottom: 3, left: 10 };

  return (
    <AutoSizer>
      {({ height, width }) => {
        return (
          <div style={{ width, height }}>
            <ResponsiveBar
              data-testid={name + 'BarChart'}
              data={clonedData}
              keys={['y']}
              indexBy="x"
              tooltip={({ value, indexValue }) => {
                return (
                  <div className={classes.tooltipBar}>
                    {timeFormat('%B %Y')(new Date(indexValue))}: {value}
                  </div>
                );
              }}
              theme={theme}
              margin={margin}
              groupMode="grouped"
              padding={0.25}
              axisTop={null}
              axisRight={null}
              axisBottom={axisBottom}
              axisLeft={axisLeft}
              enableGridY={enableGridY}
              enableLabel={false}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
              legends={[]}
              animate={true}
              motionStiffness={90}
              motionDamping={15}
              colors={(d) => d.data.color}
              gridYValues={4}
            />
          </div>
        );
      }}
    </AutoSizer>
  );
};

export default BarChart;
